<template>
  <section class="py-4">
    <div class="row">
      <div class="col-md-12">
        <!-- Tab Navigation -->
        <div class="card mb-4">
          <div class="card-header p-0">
            <ul class="nav nav-tabs card-header-tabs" id="auditTabs" role="tablist">
              <li class="nav-item" role="presentation">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTab === 'new-audit' }"
                  @click="switchTab('new-audit')"
                  href="#"
                  role="tab"
                >
                  <i class="fa fa-plus mr-2"></i>
                  New Audit
                </a>
              </li>
              <li class="nav-item" role="presentation">
                <a 
                  class="nav-link" 
                  :class="{ active: activeTab === 'previous-reports' }"
                  @click="switchTab('previous-reports')"
                  href="#"
                  role="tab"
                >
                  <i class="fa fa-history mr-2"></i>
                  Previous Reports
                </a>
              </li>
            </ul>
          </div>
          
          <div class="card-body">
            <!-- New Audit Tab Content -->
            <div v-if="activeTab === 'new-audit'" class="tab-content">
              <form @submit.prevent="submitAudit">
                <div class="mb-3">
                  <input
                      v-model="urlInput"
                      placeholder="Enter Domain Name or URL (e.g. domain.com/page)"
                      class="form-control"
                  />
                </div>

                <div class="custom-control custom-checkbox">
                  <input type="checkbox" v-model="adblockChecked" class="custom-control-input" id="AdBlock"/>
                  <label class="custom-control-label pt-3" for="AdBlock">Enable AdBlock</label>
                </div>
                
                <button
                    class="btn btn-primary"
                    type="submit"
                    :disabled="loading"
                >
                  <span v-if="loading" class="fa fa-spinner fa-spin"></span>
                  <span v-else>Run Audit</span>
                </button>
              </form>
            </div>

            <!-- Previous Reports Tab Content -->
            <div v-if="activeTab === 'previous-reports'" class="tab-content">
              <!-- Search + Date Range Filters -->
              <div class="card bg-light border-0 mb-4">
                <div class="card-body p-3">
                  <div class="search-box-wrapper mb-3">
                    <i class="fa fa-search search-box-icon"></i>
                    <input
                      v-model="searchTerm"
                      @input="onSearchInput"
                      @keyup.enter="applyFilters"
                      type="text"
                      class="form-control search-box-input"
                      placeholder="Search by domain or URL..."
                    />
                  </div>
                  <div class="d-flex flex-wrap align-items-end">
                    <div class="mr-3 mb-2">
                      <label class="small text-muted mb-1">From Date</label>
                      <input
                        v-model="dateFrom"
                        type="date"
                        class="form-control"
                        :max="dateTo || maxDate"
                      />
                    </div>
                    <div class="mr-3 mb-2">
                      <label class="small text-muted mb-1">To Date</label>
                      <input
                        v-model="dateTo"
                        type="date"
                        class="form-control"
                        :min="dateFrom"
                        :max="maxDate"
                      />
                    </div>
                    <div class="mb-2 filter-actions">
                      <label class="small text-muted mb-1 d-block invisible">Search</label>
                      <button
                        class="btn btn-primary filter-btn"
                        type="button"
                        :disabled="isSearching"
                        @click="applyFilters"
                      >
                        <span v-if="isSearching" class="fa fa-spinner fa-spin mr-1"></span>
                        <i v-else class="fa fa-search mr-1"></i>
                        Search
                      </button>
                      <button
                        v-if="searchTerm || dateFrom || dateTo || filtersApplied"
                        class="btn btn-outline-secondary filter-btn"
                        type="button"
                        @click="clearFilters"
                      >
                        <i class="fa fa-times mr-1"></i>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="loadingReports || isSearching" class="text-center py-4">
                <span class="fa fa-spinner fa-spin"></span>
                <span class="ml-2">{{ isSearching ? 'Searching...' : 'Loading reports...' }}</span>
              </div>
              <div v-else-if="filteredReports.length === 0 && !filtersApplied" class="text-center py-4 text-muted">
                <i class="fa fa-inbox fa-3x mb-3 d-block"></i>
                <p>No previous reports found.</p>
                <button class="btn btn-primary" @click="switchTab('new-audit')">
                  <i class="fa fa-plus mr-2"></i>
                  Create Your First Report
                </button>
              </div>
              <div v-else-if="filteredReports.length === 0 && filtersApplied" class="text-center py-4 text-muted">
                <i class="fa fa-search fa-3x mb-3 d-block"></i>
                <p>No reports found{{ appliedSearchTerm ? ` for "${appliedSearchTerm}"` : '' }}{{ appliedDateFrom || appliedDateTo ? ' in the selected date range' : '' }}</p>
                <button class="btn btn-outline-primary" @click="clearFilters">
                  <i class="fa fa-times mr-2"></i>
                  Clear Filters
                </button>
              </div>
              <div v-else class="reports-list" style="max-height: 500px; overflow-y: auto;">
                <div v-if="filtersApplied" class="mb-3">
                  <small class="text-muted">
                    <i class="fa fa-info-circle mr-1"></i>
                    Showing {{ filteredReports.length }} result(s){{ appliedSearchTerm ? ` for "${appliedSearchTerm}"` : '' }}
                  </small>
                </div>
                <div v-else class="mb-3">
                  <small class="text-muted">
                    <i class="fa fa-info-circle mr-1"></i>
                    Showing latest {{ filteredReports.length }} report(s)
                  </small>
                </div>
                <div 
                  v-for="report in filteredReports" 
                  :key="report.id"
                  class="report-item border rounded p-3 mb-3 cursor-pointer"
                  @click="loadReport(report)"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <b class="mb-1">{{ report.siteUrl || report.domain }}</b><br>
                      <small class="text-muted">
                        <i class="fa fa-calendar mr-1"></i>
                        {{ formatDate(report.createdAt) }}
                      </small>
                      <br>
                      <small class="text-muted">
                        <i class="fa fa-user mr-1"></i>
                        {{ report.addedBy }}
                      </small>
                    </div>
                    <div class="text-right">
                      <button
                        class="btn btn-sm btn-outline-primary mr-2"
                        @click.stop="loadReport(report)"
                      >
                        View Report
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        :disabled="deletingReportId === report.id"
                        @click.stop="deleteReport(report)"
                      >
                        <span v-if="deletingReportId === report.id" class="fa fa-spinner fa-spin"></span>
                        <i v-else class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!loading && (parsedReport.length || gtmetrixData || wpoDomain)" id="reportContent">
      <div class="row">
        <div class="col-md-12">
          <hr class="my-10"/>
          <div class="mb-4">
            <div>
              <button class="btn  btn-brand shadow download-pdf-btn d-flex align-items-center px-3  py-2 mr-3 mb-5" @click="downloadPdf">
              <span class="pdf-icon-wrapper d-flex align-items-center justify-content-center mr-2">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="6" fill="#fff"/>
                  <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8.82843C20 8.29799 19.7893 7.78929 19.4142 7.41421L15.5858 3.58579C15.2107 3.21071 14.702 3 14.1716 3H6ZM13 3.5V8C13 8.55228 13.4477 9 14 9H18.5L13 3.5Z" fill="#1f4bf1"/>
                  <path d="M9 15H15V17H9V15Z" fill="#1f4bf1"/>
                </svg>
              </span>
                <span class="font-weight-bold" style="">Download Report as PDF</span>
          </button>
            </div><br>
            <div class="share-link-card card border-0 shadow-sm mb-0 flex-grow-1" style="min-width: 260px;">
              <div class="card-body d-flex flex-column flex-md-row align-items-md-center p-3">
              <div class="share-link-icon d-flex align-items-center justify-content-center mr-md-4 mb-3 mb-md-0">
                <i class="fa fa-link"></i>
              </div>
              <div class="flex-grow-1">
                <label class="font-weight-bold mb-2" style="color: #1f4bf1;">Shareable Link:</label>
                <a href="javascript:void(0)" @click.prevent="copyLink"><i class="fa fa-copy pull-right"></i></a>
                <div class="input-group mb-2">
                  <input
                      type="text"
                      class="form-control border-0 bg-light"
                      :value="generatedLink"
                      readonly
                        style="font-size: 1.05rem; color: #1f4bf1; font-weight: 500; background: #f5f8ff; border-radius: 6px;"
                  />
                </div>
                <small class="form-text" style="color: #6c757d;">
                  Share this link to allow others to view this performance report.
                </small>
              </div>
            </div>
          </div>
          </div>
          <hr class="my-10"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 p-0">
          <section class="p-0 m-0" id="reportContent" style=" margin: auto;">

            <div class="main-page">

              <div class="p-3 ">
                <!-- Header Section -->
                <header class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-20  border-bottom" style=" padding-bottom: 1rem;">
                  <img src="https://www.bigscoots.com/wp-content/uploads/2022/10/bigscoots-logo-dark.svg" alt="BigScoots Logo" class="img-fluid" style="height: 40px;">
                  <div class="text-sm-right">
                    <h3 class=" m-0 font-weight-bold">Website Performance Audit</h3>
                  </div>
                </header>

                <!-- Report Details Section -->
                <div class="card-section p-4 mb-5">
                  <h4 class=" font-weight-bold mb-3">Report Overview</h4>
                  <div class="row">
                    <div class="col-12  d-flex align-items-center mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" class=" mr-5 text-secondary" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                      <span class="font-weight-medium text-secondary-600 mr-5">URL Tested:</span>
                      <a :href="siteUrl" target="_blank" class="text-primary">{{ siteUrl }}</a>
                    </div>
                    <div class="col-12 col-md-6 d-flex align-items-center mt-10">
                      <svg xmlns="http://www.w3.org/2000/svg" class="bi mr-5 text-secondary" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      <span class="font-weight-medium text-secondary-600 mr-5">Prepared by:</span>
                      <img  :src="`https://www.gravatar.com/avatar/${displayAuthorEmailHash}?s=24`"   :alt="`${displayAuthor} avatar`" class="rounded-circle mr-5" style="width: 24px; height: 24px;">
                      <span>{{ displayAuthor }}</span>
                    </div>
                    <div class="col-12 col-md-6 d-flex align-items-center mb-3 mt-10">
                      <svg xmlns="http://www.w3.org/2000/svg" class="bi mr-5 text-secondary" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      <span class="font-weight-medium text-secondary-600 mr-5">Report Date:</span>
                      <span id="report-date">{{ displayReportDate }}</span>
                    </div>
                  </div>

                  <!-- Tests and Analysis Section -->
                  <div class="mt-10 pt-10 border-top">
                    <h5 class=" font-weight-medium mb-2">Tests & Analysis Conducted</h5>
                    <ul class="row">
                      <li class="col-12 col-sm-6 d-flex align-items-center mb-2">
                        <svg v-if="parsedReport.length" class="bi mr-5 text-success" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        <span v-else>
                        <i class="fa fa-warning text-warning mr-5" style="width: 20px; height: 20px"></i>
                      </span>
                        Field Performance Metrics (Core Web Vitals)
                      </li>
                      <li class="col-12 col-sm-6 d-flex align-items-center mb-2">
                        <svg v-if="gtmetrixData" class="bi mr-5 text-success" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        <span v-else>
                        <i class="fa fa-warning text-warning mr-5" style="width: 20px; height: 20px"></i>
                      </span>
                        Lab Performance Metrics (GTmetrix)
                      </li>
                      <li class="col-12 col-sm-6 d-flex align-items-center mb-2">
                        <svg v-if="wpoData && wpoData.success && wpoData.wpo_data && wpoData.wpo_data.domains && wpoData.wpo_data.domains.length" class="bi mr-5 text-success" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        <span v-else>
                        <i class="fa fa-warning text-warning mr-5" style="width: 20px; height: 20px"></i>
                      </span>
                        WordPress Core & Environment
                      </li>
                      <li class="col-12 col-sm-6 d-flex align-items-center mb-2">
                        <svg v-if="wpoData && wpoData.success && wpoData.wpo_data && wpoData.wpo_data.domains && wpoData.wpo_data.domains.length && wpoData.wpo_data.domains[0].plugins && wpoData.wpo_data.domains[0].plugins.length" class="bi mr-5 text-success" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        <span v-else>
                        <i class="fa fa-warning text-warning mr-5" style="width: 20px; height: 20px"></i>
                      </span>
                        WordPress Plugins
                      </li>
                      <li class="col-12 col-sm-6 d-flex align-items-center mb-2">
                        <svg v-if="wpoData && wpoData.success && wpoData.wpo_data && wpoData.wpo_data.domains && wpoData.wpo_data.domains.length && wpoData.wpo_data.domains[0].themes && wpoData.wpo_data.domains[0].themes.length" class="bi mr-5 text-success" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        <span v-else>
                        <i class="fa fa-warning text-warning mr-5" style="width: 20px; height: 20px"></i>
                      </span>
                        WordPress Themes
                      </li>
                      <li class="col-12 col-sm-6 d-flex align-items-center mb-2">
                        <svg v-if="wpoData && wpoData.success && wpoData.wpo_data && wpoData.wpo_data.domains && wpoData.wpo_data.domains.length && wpoData.wpo_data.domains[0].plugins && wpoData.wpo_data.domains[0].plugins.length" class="bi mr-5 text-success" width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
                        <span v-else>
                        <i class="fa fa-warning text-warning mr-5" style="width: 20px; height: 20px"></i>
                      </span>
                        Security Vulnerability Checks
                      </li>
                    </ul>
                  </div>

                </div>

                <!-- Key Findings & Recommendations Section -->
                <div class="card-section px-4 mb-5" :class="!uodatedKeyFindings ? 'no-print' : ''">
                  <button data-toggle="modal"  @click="fixModal" data-target="#newKeyFindingsModal" class="btn btn-outline-primary pull-right no-print"><i class="fa fa-bullhorn"></i> Add / Update Findings</button>

                  <h3 class="h4 font-weight-medium mb-3">Key Findings & Recommendations</h3>

                  <div class="text-secondary" v-html="uodatedKeyFindings" />
                </div>
                <div>
                  <!-- CRUX Metrics -->
                  <div v-if="parsedReport.length" id="crux" class="pdf-break-before card-section p-4 mb-5">
                    <div class="p-2">
                      <h5 class="report-section-heading mb-0">CRUX Metrics</h5>
                      <div class="mb-3">
                        <p class="text-secondary">Core Web Vitals are the metrics that Google uses to evaluate page experience. They measure loading, interactivity, and visual stability.</p>
                      </div>

                      <div class="alert alert-primary" style="background-color: #1f4bf1; font-size: 12px" role="alert">
                        This data comes from the Chrome User Experience Report (CrUX), which collects real performance metrics from actual Chrome users visiting this site. It provides valuable insights into how your website performs across different devices and network conditions. Please note that any recent optimizations won't appear immediately, as Google only updates CrUX data on the second Tuesday of every month.
                      </div>

                      <div class="my-3 report-text-small">
                        <span class="badge bg-green-100 text-green-700 mr-2">Good</span>
                        <span class="badge bg-yellow-100 text-yellow-700 mr-2 "  >Needs Improvement</span>
                        <span class="badge bg-red-100 text-red-700">Poor</span>
                      </div>

                      <!-- Data Collection Periods -->
                      <div class="data-collection-periods mb-4 p-4 rounded border">
                        <div class="d-flex align-items-center mb-3">
                          <i class="fa fa-calendar mr-2 text-primary" style="font-size: 1.1rem;"></i>
                          <h6 class="data-collection-title font-weight-bold mb-0">Data Collection Periods</h6>
                        </div>
                        <div class="data-collection-content">
                          <div class="d-flex align-items-center mb-2">
                            <i class="fa fa-desktop mr-2 text-primary" style="font-size: 1rem; min-width: 20px;"></i>
                            <span class="text-dark"><strong>Desktop</strong> <span class="ml-1">{{ getDataCollectionPeriod() }}</span></span>
                          </div>
                          <div class="d-flex align-items-center mb-3 mt-3">
                            <i class="fa fa-mobile mr-2 text-primary" style="font-size: 1.5rem; min-width: 20px;"></i>
                            <span class="text-dark"><strong>Mobile</strong> <span class="ml-1">{{ getDataCollectionPeriod() }}</span></span>
                          </div>
                          <div class="mt-2">
                            <span class="text-dark"><strong>Origin Data:</strong> <span class="origin-data-highlight ml-1">Covers all webpages in the website.</span></span>
                          </div>
                        </div>
                      </div>

                      <!-- Core Vitals Cards - Mobile and Desktop Comparison -->
                      <div class="row my-10">
                        <div
                            class="col-12 col-md-4 mb-4 mb-md-0"
                            v-for="metric in ['lcp', 'inp', 'cls']"
                            :key="metric"
                        >
                          <div class="core-vital-card border rounded p-4 h-100 d-flex flex-column">
                            <div class="text-center mb-4">
                              <h3 class="font-weight-bold h5 mb-2">{{ getMetricTitle(metric) }}</h3>
                              <p class="small text-secondary mb-1">{{ getCruxMetric(metric, 'mobile').description }}</p>
                              <p class="text-xs text-secondary" v-html="getCruxMetric(metric, 'mobile').note"></p>
                            </div>
                            
                            <!-- Mobile and Desktop Metrics Side by Side -->
                            <div class="d-flex justify-content-around align-items-center mb-3">
                              <!-- Mobile Metric -->
                              <div class="text-center">
                                <div
                                    class="metric-circle-large mx-auto mb-2"
                                    :class="{
                                      'bg-green-100': getCruxBadge(metric, 'mobile').level === 'Good',
                                      'bg-yellow-100': getCruxBadge(metric, 'mobile').level === 'Needs Improvement',
                                      'bg-red-100': getCruxBadge(metric, 'mobile').level === 'Poor'
                                    }"
                                >
                                  <span
                                      class="metric-value-large font-weight-bold"
                                      :class="{
                                        'text-green-700': getCruxBadge(metric, 'mobile').level === 'Good',
                                        'text-yellow-700': getCruxBadge(metric, 'mobile').level === 'Needs Improvement',
                                        'text-red-700': getCruxBadge(metric, 'mobile').level === 'Poor'
                                      }"
                                  >
                                    {{ getCruxMetric(metric, 'mobile').p75 }}
                                  </span>
                                </div>
                                <div class="device-label text-uppercase font-weight-bold text-muted">Mobile</div>
                              </div>

                              <!-- Desktop Metric -->
                              <div class="text-center">
                                <div
                                    class="metric-circle-large mx-auto mb-2"
                                    :class="{
                                      'bg-green-100': getCruxBadge(metric, 'desktop').level === 'Good',
                                      'bg-yellow-100': getCruxBadge(metric, 'desktop').level === 'Needs Improvement',
                                      'bg-red-100': getCruxBadge(metric, 'desktop').level === 'Poor'
                                    }"
                                >
                                  <span
                                      class="metric-value-large font-weight-bold"
                                      :class="{
                                        'text-green-700': getCruxBadge(metric, 'desktop').level === 'Good',
                                        'text-yellow-700': getCruxBadge(metric, 'desktop').level === 'Needs Improvement',
                                        'text-red-700': getCruxBadge(metric, 'desktop').level === 'Poor'
                                      }"
                                  >
                                    {{ getCruxMetric(metric, 'desktop').p75 }}
                                  </span>
                                </div>
                                <div class="device-label text-uppercase font-weight-bold text-muted">Desktop</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Additional Metrics - Desktop and Mobile -->

                      <!-- Additional Metrics -->
                      <div style="margin: 20px 0 20px 0">
                        <h3 class="h5 font-weight-semibold text-dark mb-3">Additional Metrics</h3>
                        <div class="d-flex justify-content-between align-items-center" >
                          <div
                              class="d-flex justify-content-between align-items-center bg-light p-3 rounded border mb-3"
                              v-if="getCruxMetric('ttfb', 'mobile').p75 !== 'N/A'"
                              style="width: 49%; height: 100%"
                          >
                            <div>
                              <p class="font-weight-medium text-dark mb-1">Time to First Byte (TTFB)</p>
                              <p class="small text-muted mb-0">Measures server response time.</p>
                            </div>
                            <div class="text-right">
                              <p class="h5 font-weight-bold mb-1" :class="getMetricColorClass('ttfb', 'mobile', 0.8, 1.2)">{{ getCruxMetric('ttfb', 'mobile').p75 }}</p>
                              <p class="small text-muted mb-0">Target: &lt; 0.8s</p>
                            </div>
                          </div>
                          <div
                              class="d-flex justify-content-between align-items-center bg-light p-3 rounded border"
                              v-if="getCruxMetric('fcp', 'mobile').p75 !== 'N/A'"
                              style="width: 49%; height: 100%"
                          >
                            <div>
                              <p class="font-weight-medium text-dark mb-1">First Contentful Paint (FCP)</p>
                              <p class="small text-muted mb-0">Marks the first point in the page load timeline.</p>
                            </div>
                            <div class="text-right" style="width: 30%">
                              <p class="h5 font-weight-bold mb-1" :class="getMetricColorClass('fcp', 'mobile', 1.8, 3.0)">{{ getCruxMetric('fcp', 'mobile').p75 }}</p>
                              <p class="small text-muted mb-0">Target: &lt; 1.8s</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-for="(metric, idx) in combinedMetrics" :key="idx" class="mb-4">
                        <hr>
                        <button @click="openNotesModal(metric.type)" class="no-print btn btn-sm btn-default pull-right"> <i class="fa fa-plus"></i> Notes</button>
                        <h6>{{ metric.title }}  - Overview</h6>
                        <p class="text-secondary">{{ metric.description }}</p>
                        <div class="row">
                          <div class="col-md-12 mb-2 report-text-small" >
                            <!-- Desktop -->
                            <div :class="idx < combinedMetrics.length - 1 ? 'mb-3' : '' ">
                              <div class="fw-bold mb-1">Desktop (%)</div>
                              <div class="progress rounded-pill" style="height: 30px;">
                                <div
                                    v-for="(bucket, i) in metric.desktop.histogram"
                                    :key="i"
                                    class="progress-bar"
                                    :class="getBarClass(i)"
                                    :style="{ width: bucket.density * 100 + '%' }"
                                >
                                  {{ (bucket.density * 100).toFixed(1) }}
                                </div>
                              </div>
                              <small class="text-muted">P75: {{ metric.desktop.p75 }}</small>
                            </div>
                          </div>

                          <div class="col-md-12" :class="idx < combinedMetrics.length ? 'mb-2' : ''">
                            <div>
                              <div class="fw-bold mb-1">Mobile (%)</div>
                              <div class="progress rounded-pill" style="height: 30px;">
                                <div
                                    v-for="(bucket, i) in metric.mobile.histogram"
                                    :key="i"
                                    class="progress-bar"
                                    :class="getBarClass(i)"
                                    :style="{ width: bucket.density * 100 + '%' }"
                                >
                                  {{ (bucket.density * 100).toFixed(1) }}
                                </div>
                              </div>
                              <small class="text-muted">P75: {{ metric.mobile.p75 }}</small>
                            </div>
                          </div>
                        </div>
                        <div class="card shadow-sm mb-4" v-if="comments[metric.type].length">
                          <div class="card-header py-2 bg-primary text-white d-flex justify-content-between align-items-center">
                            <h6 class="mb-0">Comments</h6>
                            <!--                            <span class="badge bg-light text-dark">{{ comments[metric.type].length }}</span>-->
                          </div>
                          <div class="card-body p-3" style="max-height: 300px; overflow-y: auto;">
                            <div
                                v-for="(comment, index) in comments[metric.type]"
                                :key="index"
                                class="mb-3 pb-2"
                                :class="index < comments[metric.type].length - 1 ? 'border-bottom' : ''"
                            >
                              <p class="mb-1 text-body">
                                <i class="bi bi-chat-left-text-fill text-primary me-2"></i>
                                <span v-html="comment.text" />
                              </p>
                              <div class="d-flex justify-content-between small text-muted">
                                <span><i class="bi bi-person-fill me-1"></i>{{ comment.addedBy }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="card-section p-4 mb-5">
                    <div class="mt-10">
                      <div class="alert alert-warning text-dark" role="alert"  style="background-color: #f1b433; font-size: 12px">
                        <strong>Note:</strong> Real CrUX data is not available for this domain. Showing estimated values for demonstration purposes.
                      </div>
                    </div>
                  </div>
                  <!-- GTmetrix Report -->
                  <div v-if="gtmetrixData" id="gtmetrix" class="pdf-break-before card-section px-4 my-2 ">
                    <div class="p-2">
                      <h5 class="h4 mb-0">
                        <b>GTmetrix Report</b>
                      </h5>
                      <button @click="openNotesModal('gtmatrics')" class="no-print btn btn-sm btn-default pull-right"> <i class="fa fa-plus"></i> Notes</button>
                    </div>
                    <div class="p-2">
                      <p class="report-text-small">
                        <a
                            :href="gtmetrixData.report_url"
                            target="_blank"
                            class="card-link"
                        >
                          📊 View Full Report
                        </a>
                        |
                        <a
                            :href="gtmetrixData.pdf_url"
                            target="_blank"
                            class="card-link"
                        >
                          📄 Download PDF
                        </a>
                      </p>
                      <div class="row py-3" >
                        <div class="col-md-5 ">
                          <div class="bg-light w-100 p-2">
                            <div class="d-flex align-items-center justify-content-around w-100">
                              <div class="d-flex align-items-center flex-column">
                                <div
                                    class="circular-progress"
                                    :style="{ background: getProgressStyle(gtmetrixData.performance_score) }"
                                >
                                  <div class="circular-inner">
                                    <div class="score-text">{{ gtmetrixData.performance_score }}%</div>
                                  </div>
                                </div>
                                <div><b>Performance</b></div>

                              </div>
                              <div class="d-flex align-items-center flex-column">
                                <div
                                    class="circular-progress"
                                    :style="{ background: getProgressStyle(gtmetrixData.structure_score) }"
                                >
                                  <div class="circular-inner">
                                    <div class="score-text">{{ gtmetrixData.structure_score }}%</div>
                                  </div>
                                </div>
                                <div><b>Structure</b></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-7 p-0" style="align-content: center">
                          <div class="my-auto d-flex flex-column justify-content-between w-100 h-100">
                            <div>
                              <div class="row">
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light  ">
                                    <strong>LCP:</strong><br> {{ (gtmetrixData.lcp / 1000).toFixed(1) }} s
                                  </div>
                                </div>
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light ">
                                    <strong>CLS:</strong><br> {{ gtmetrixData.cls.toFixed(2) }}
                                  </div>
                                </div>
                                <div class="col-md-3  ">
                                  <div class="text-center bg-light ">
                                    <strong>TBT:</strong> <br>{{ gtmetrixData.tbt }} ms
                                  </div>
                                </div>
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light ">
                                    <strong>TTFB:</strong><br> {{ gtmetrixData.ttfb }} ms
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div class="row">
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light ">
                                    <strong>Speed Index:</strong><br> {{ gtmetrixData.speed_index }} ms
                                  </div>
                                </div>
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light ">
                                    <strong>Fully Loaded:</strong><br> {{ (gtmetrixData.fully_loaded_time / 1000).toFixed(1) }} s
                                  </div>
                                </div>
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light ">
                                    <strong>Page Requests:</strong> <br>{{ gtmetrixData.page_requests }}
                                  </div>

                                </div>
                                <div class="col-md-3 ">
                                  <div class="text-center bg-light ">
                                    <strong>Page Size:</strong> <br>{{ (gtmetrixData.page_bytes / 1024).toFixed(1) }} KB
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="gtmetrixData.recommendations && gtmetrixData.recommendations.length" style="margin-top: 30px">
                        <h6>Recommendations:</h6>
                        <ul class="list-group report-text-small">
                          <li
                              v-for="(rec, i) in gtmetrixData.recommendations"
                              :key="i"
                              class="list-group-item"
                          >
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>{{ rec.issue }}</strong>
                                <p class="mb-1">
                                  <small>Affects: <span class="fw-bold">{{ rec.affects }}</span></small>
                                </p>
                              </div>
                              <span class="badge" :class="rec.severity.toLowerCase().includes('med') ? 'bg-warning' : rec.severity.toLowerCase().includes('high') ? 'bg-danger' : 'bg-default'">{{ rec.severity }}</span>
                            </div>
                            <p class="mb-1">{{ rec.solution }}</p>
                            <p v-if="rec.details" class="mb-0 text-muted">
                              <small>{{ rec.details }}</small>
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div class="card shadow-sm mb-4" v-if="comments['gtmatrics'].length">
                    <div class="card-header py-2 bg-primary text-white d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">Comments</h6>
                      <!--                    <span class="badge bg-light text-dark">{{ comments['gtmatrics'].length }}</span>-->
                    </div>
                    <div class="card-body p-3" style="max-height: 300px; overflow-y: auto;">
                      <div
                          v-for="(comment, index) in comments['gtmatrics']"
                          :key="index"
                          class="mb-3  pb-2"
                          :class="index < comments['gtmatrics'].length - 1 ? 'border-bottom' : ''"
                      >
                        <p class="mb-1 text-body">
                          <i class="bi bi-chat-left-text-fill text-primary me-2"></i>
                          <span v-html="comment.text" />
                        </p>
                        <div class="d-flex justify-content-between small text-muted">
                          <span><i class="bi bi-person-fill me-1"></i>{{ comment.addedBy }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="wpoDomain" id="wpo" class="card-section p-4 mb-5 pdf-break-before mt-4">
                    <div class="py-2 align-items-center">
                      <button @click="openNotesModal('plugins')" class="no-print btn btn-sm btn-default pull-right"> <i class="fa fa-plus"></i> Notes</button>
                      <h5 class="mb-0"> <b>WordPress Optimization</b></h5>
                    </div>
                    <div v-if="hasWpoData" class="text-secondary">
                      <p><strong>IP Address:</strong> {{ wpoIP || 'NA' }}</p>
                      <p><strong>PHP Version:</strong> {{ phpVer || 'NA' }}</p>
                      <p><strong>WordPress Version:</strong> {{ wpVer || 'NA' }}</p>

                      <div class="card shadow-sm mb-4" v-if="comments['plugins'].length">
                        <div class="card-header py-2 bg-primary text-white d-flex justify-content-between align-items-center">
                          <h6 class="mb-0">Comments</h6>
                          <!--                    <span class="badge bg-light text-dark">{{ comments['plugins'].length }}</span>-->
                        </div>
                        <div class="card-body p-3" style="max-height: 300px; overflow-y: auto;">
                          <div
                              v-for="(comment, index) in comments['plugins']"
                              :key="index"
                              class="mb-3  pb-2"
                              :class="index < comments['plugins'].length - 1 ? 'border-bottom' : ''"
                          >
                            <p class="mb-1 text-body">
                              <i class="bi bi-chat-left-text-fill text-primary me-2"></i>
                              <span v-html="comment.text" />
                            </p>
                            <div class="d-flex justify-content-between small text-muted">
                              <span><i class="bi bi-person-fill me-1"></i>{{ comment.addedBy }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="py-2 align-items-center">
                        <button @click="openNotesModal('pluginlist')" class="no-print btn btn-sm btn-default pull-right"> <i class="fa fa-plus"></i> Notes</button>
                        <h4 class="mt-4">Active Plugins ({{ safePlugins.length }})</h4>
                      </div>
                      <div class="table-responsive">
                        <table class="table table-hover table-bordered table-sm">
                          <thead class="thead-light">
                          <tr>
                            <th width="25%">Plugins</th>
                            <th class="text-center">Version</th>
                            <th class="text-center">Status</th>
                            <th class="text-center">Security</th>
                            <th class="text-center" width="25%">Comment</th>
                          </tr>
                          </thead>
                          <tbody  class="text-secondary" v-for="(plugin, index) in plugins" :key="plugin.name">
                          <tr>
                            <td>{{ plugin.name }}</td>

                            <td class="text-center">
                              {{ plugin.version }}
                            </td>
                            <td class="text-center">
                          <span :class="['badge', plugin.status === 'active' ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100']">
                            {{ plugin.status === 'active' ? 'Active' : 'Inactive' }}
                          </span>
                            </td>

                            <!--                      <td class="text-center">-->
                            <!--                        {{ plugin.version }}-->
                            <!--                      </td>-->

                            <td class="text-center">
                              <template>
                          <span
                              class="badge"
                              :class="getVulnerabilityClass(plugin.security)"
                          >
                            {{Array.isArray(plugin.security) ? 'vulnerable' : plugin.security }}
                          </span>
                              </template>
                            </td>
                            <td class="text-center plugin-comment-cell">
                              <div class="d-flex align-items-center justify-content-center">
                                <div class="flex-grow-1 text-left mr-2">
                                  <span 
                                    v-if="plugin.comment && plugin.comment.text"
                                    class="plugin-comment-text"
                                    :title="stripHtml(plugin.comment.text)"
                                  >
                                    {{ stripHtml(plugin.comment.text) }}
                                  </span>
                                  <span v-else class="text-muted small font-italic">No comment</span>
                                </div>
                                <button 
                                  class="btn btn-sm btn-outline-primary no-print plugin-comment-btn"
                                  @click="openPluginCommentModal(plugin)"
                                  :title="plugin.comment ? 'Edit comment' : 'Add comment'"
                                  style="min-width: 35px; padding: 0.25rem 0.5rem; flex-shrink: 0;"
                                >
                                  <i :class="plugin.comment ? 'fa fa-edit' : 'fa fa-plus'"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          <tr v-if="Array.isArray(plugin.security)">
                            <td colspan="5">
                              <div
                                  class="alert alert-danger mb-3"
                              >
                                <strong>{{ plugin.name }}</strong>
                                <div
                                    v-for="(issue, i) in plugin.security"
                                    :key="i"
                                    class="mt-2"
                                >
                                  <div><strong>{{ issue.title }}</strong></div>
                                  <small>
                                    {{ issue.description }}<br />
                                    <strong>Severity:</strong> {{ issue.severity }}<br />
                                    <strong>Affected Version:</strong> {{ issue.affected_version || 'Unknown' }}
                                  </small>
                                </div>
                              </div>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div v-else class="card-body">
                      <p>No WPO data available.</p>
                    </div>
                  </div>
                  <div class="card shadow-sm mb-4" v-if="comments['pluginlist'] && comments['pluginlist'].length">
                    <div class="card-header py-2 bg-primary text-white d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">Comments</h6>
                      <!--                    <span class="badge bg-light text-dark">{{ comments['plugins'].length }}</span>-->
                    </div>
                    <div class="card-body p-3" style="max-height: 300px; overflow-y: auto;">
                      <div
                          v-for="(comment, index) in comments['pluginlist']"
                          :key="index"
                          class="mb-3  pb-2"
                          :class="index < comments['pluginlist'].length - 1 ? 'border-bottom' : ''"
                      >
                        <p class="mb-1 text-body">
                          <i class="bi bi-chat-left-text-fill text-primary me-2"></i>
                          <span v-html="comment.text" />
                        </p>
                        <div class="d-flex justify-content-between small text-muted">
                          <span><i class="bi bi-person-fill me-1"></i>{{ comment.addedBy }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Themes Section -->
                  <div v-if="hasWpoData && themes.length > 0" class="py-2 align-items-center mt-5">
                    <button @click="openNotesModal('themelist')" class="no-print btn btn-sm btn-default pull-right"> <i class="fa fa-plus"></i> Notes</button>
                    <h4 class="mt-4">Themes ({{ themes.length }})</h4>
                  </div>
                  <div v-if="hasWpoData && themes.length > 0" class="table-responsive">
                    <table class="table table-hover table-bordered table-sm">
                      <thead class="thead-light">
                      <tr>
                        <th width="25%">Theme</th>
                        <th class="text-center">Version</th>
                        <th class="text-center">Status</th>
                        <th class="text-center">Security</th>
                        <th class="text-center" width="25%">Comment</th>
                      </tr>
                      </thead>
                      <tbody class="text-secondary" v-for="(theme, index) in themes" :key="theme.name">
                      <tr>
                        <td>{{ theme.title || theme.name }}</td>

                        <td class="text-center">
                          {{ theme.version }}
                        </td>
                        <td class="text-center">
                          <span :class="['badge', getThemeStatusClass(theme.status)]">
                            {{ getThemeStatusDisplay(theme.status) }}
                          </span>
                        </td>

                        <td class="text-center">
                          <template>
                            <span
                                class="badge"
                                :class="getThemeSecurityClass(theme.security)"
                            >
                              {{ getThemeSecurityDisplay(theme.security) }}
                            </span>
                          </template>
                        </td>
                        <td class="text-center plugin-comment-cell">
                          <div class="d-flex align-items-center justify-content-center">
                            <div class="flex-grow-1 text-left mr-2">
                              <span 
                                v-if="theme.comment && theme.comment.text"
                                class="plugin-comment-text"
                                :title="stripHtml(theme.comment.text)"
                              >
                                {{ stripHtml(theme.comment.text) }}
                              </span>
                              <span v-else class="text-muted small font-italic">No comment</span>
                            </div>
                            <button 
                              class="btn btn-sm btn-outline-primary no-print plugin-comment-btn"
                              @click="openThemeCommentModal(theme)"
                              :title="theme.comment ? 'Edit comment' : 'Add comment'"
                              style="min-width: 35px; padding: 0.25rem 0.5rem; flex-shrink: 0;"
                            >
                              <i :class="theme.comment ? 'fa fa-edit' : 'fa fa-plus'"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="Array.isArray(theme.security)">
                        <td colspan="5">
                          <div
                              class="alert alert-danger mb-3"
                          >
                            <strong>{{ theme.title || theme.name }}</strong>
                            <div
                                v-for="(issue, i) in theme.security"
                                :key="i"
                                class="mt-2"
                            >
                              <div><strong>{{ issue.title }}</strong></div>
                              <small>
                                {{ issue.description }}<br />
                                <strong>Severity:</strong> {{ issue.severity }}<br />
                                <strong>Affected Version:</strong> {{ issue.affected_version || 'Unknown' }}
                              </small>
                            </div>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="card shadow-sm mb-4" v-if="comments['themelist'] && comments['themelist'].length">
                    <div class="card-header py-2 bg-primary text-white d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">Comments</h6>
                    </div>
                    <div class="card-body p-3" style="max-height: 300px; overflow-y: auto;">
                      <div
                          v-for="(comment, index) in comments['themelist']"
                          :key="index"
                          class="mb-3  pb-2"
                          :class="index < comments['themelist'].length - 1 ? 'border-bottom' : ''"
                      >
                        <p class="mb-1 text-body">
                          <i class="bi bi-chat-left-text-fill text-primary me-2"></i>
                          <span v-html="comment.text" />
                        </p>
                        <div class="d-flex justify-content-between small text-muted">
                          <span><i class="bi bi-person-fill me-1"></i>{{ comment.addedBy }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Footer -->
                <footer class="text-center mt-5 small text-secondary">
                  <p>This report was generated by the BigScoots Performance Analysis Tool.</p>
                  <p>&copy; <span id="footer-year"></span> BigScoots. All Rights Reserved.</p>
                </footer>
              </div>
            </div>
            <!-- WPO Report -->
            <!-- Fallback -->
            <div v-if="!loading && (!parsedReport.length && !gtmetrixData)" class="alert alert-info">
              No report yet. Submit a URL above to run an audit.
            </div>
          </section>
        </div>
      </div>
    </div>
    <report-notes-modal ref="notes" />
    <plugin-comment-modal ref="pluginCommentModal" @comment-updated="onPluginCommentUpdated" />

    <!-- Modal -->
    <div class="new-findings-modal position-relative">
      <div class="modal fade newKeyFindingsModal" id="newKeyFindingsModal" tabindex="-1" role="dialog" aria-labelledby="newKeyFindingsModal" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="exampleModalLabel">{{siteUrl}}</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label>Key Findings & Recommendations</label>
                <vue-editor v-model="keyFindings" :editor-toolbar="customToolbar" />
                <h5 class="mt-3 mb-0" v-if="keyFindings !== ''">Preview</h5>
                <div v-html="keyFindings"></div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
              <button type="button" class="btn btn-primary" :disabled="loading" @click="submitKetFindings()">
                <div class="loader small_loader" v-if="loading"></div>
                <span v-else>Submit</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Domain Selection Modal -->
    <div class=" position-relative">
      <div class="modal fade" id="domainSelectionModal" tabindex="-1" role="dialog" aria-labelledby="domainSelectionModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="domainSelectionModalLabel">
                <i class="fa fa-globe mr-2"></i>
                Multiple Domains Found
              </h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="alert alert-info">
                <i class="fa fa-info-circle mr-2"></i>
                We found multiple domains with the name "<strong>{{ urlInput }}</strong>" in our system.
                Please select the specific domain you want to audit:
              </div>

              <div class="domain-selection-list">
                <div
                    v-for="(domain, index) in multipleDomains"
                    :key="index"
                    class="domain-option card mb-3"
                    :class="{ 'selected': selectedDomainIndex === index }"
                    @click="selectDomain(index)"
                >
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-1">
                        <div class="custom-control custom-radio" style="min-height: 2.5rem">
                          <input
                              type="radio"
                              :id="`domain-${index}`"
                              :value="index"
                              v-model="selectedDomainIndex"
                              class="custom-control-input"
                          >
                          <label class="custom-control-label" :for="`domain-${index}`"></label>
                        </div>
                      </div>
                      <div class="col-md-11">
                        <h6 class="mb-1">
                          <i class="fa fa-globe text-primary mr-2"></i>
                          {{ domain.domain }}
                        </h6>
                        <p class="text-muted mb-1">
                          <i class="fa fa-server mr-1"></i>
                          <strong>IP:</strong> {{ domain.ip || 'N/A' }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal">
                <i class="fa fa-times mr-2"></i>
                Cancel
              </button>
              <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="selectedDomainIndex === null"
                  @click="proceedWithSelectedDomain"
              >
                <i class="fa fa-check mr-2"></i>
                Continue with Selected Domain
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {db} from "@/firebase/init";
import swal from "sweetalert2";
import moment from "moment/moment";
import { VueEditor } from "vue2-editor";
import copyButtonMixin from "@/mixins/copyButtonMixin";
import crypto from 'crypto-js';


export default {
  name: "PerformanceAuditReport",
  mixins: [copyButtonMixin],
  components: {
    reportNotesModal: () =>
        import(
            /* webpackChunkName: "accDetailsUpdate" */ "../../../components/Admin/Cloudflare/reportNotesModal.vue"
            ),
    pluginCommentModal: () =>
        import(
            /* webpackChunkName: "accDetailsUpdate" */ "../../../components/Admin/CloudflareEnt/PluginCommentModal.vue"
            ),
    VueEditor
  },
  data() {
    return {
      urlInput: "",
      adblockChecked: false,
      domainData: null,
      loading: false,
      parsedReport: [],
      docId: null,
      gtmetrixData: null,
      generatedLink: null,
      showLinkModal: false,
      wpoData: null,
      siteUrl: null,
      crux: null,
      comments: {
        lcp: [],
        cls: [],
        inp: [],
        gtmatrics: [],
        plugins: [],
        pluginlist: [],
        themelist: [],
      },
      keyFindings: '',
      uodatedKeyFindings: '',
      customToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ script: "sub" }, { script: "super" }],
        [{ color: [] }, { background: [] }],
        ["link"],
        ["clean"]
      ],
      // New properties for tab interface and previous reports functionality
      activeTab: 'new-audit',
      previousReports: [],
      loadingReports: false,
      deletingReportId: null,
      // Properties to store original report metadata when viewing previous reports
      reportAuthor: null,
      reportAuthorEmailHash: null,
      reportCreatedDate: null,
      // Search functionality - searchTerm/dateFrom/dateTo are the live input values,
      // applied* snapshot what the current searchResults were actually queried with
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      appliedSearchTerm: '',
      appliedDateFrom: '',
      appliedDateTo: '',
      filtersApplied: false,
      searchResults: [],
      isSearching: false,
      searchTimeout: null,
      // Multiple domain selection properties
      multipleDomains: [],
      showDomainSelectionModal: false,
      selectedDomainIndex: null,
    };
  },
  computed: {
    aesSecret() {
      return this.$store.getters["crypto/aesSecret"];
    },
    gtmetrixMetrics () {
      const d = this.gtmetrixData || {};
      return {
        LCP: d.lcp ? (d.lcp / 1000).toFixed(1) + 's' : '–',
        CLS: d.cls !== undefined ? d.cls.toFixed(2) : '–',
        TBT: d.tbt ? d.tbt + 'ms' : '–',
        TTFB: d.ttfb ? (d.ttfb / 1000).toFixed(1) + 's' : '–',
        "Speed Index": d.speed_index ? (d.speed_index / 1000).toFixed(1) + 's' : '–',
        "Fully Loaded": d.fully_loaded_time ? (d.fully_loaded_time / 1000).toFixed(1) + 's' : '–',
        "Page Requests": d.page_requests || '–',
        "Page Size": d.page_bytes ? (d.page_bytes / (1024 * 1024)).toFixed(1) + ' MB' : '–'
      };
    },
    currentDate() {
      const date = new Date()
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    },
    user() {
      return this.$store.getters.user;
    },
    userEmailHash() {
      return this.$store.getters.emailHashGravatar;
    },
    // Computed properties for displaying correct report metadata
    displayAuthor() {
      return this.reportAuthor || this.user.firstName;
    },
    displayAuthorEmailHash() {
      return this.reportAuthorEmailHash || this.userEmailHash;
    },
    displayReportDate() {
      return this.reportCreatedDate || this.dateFormat(new Date());
    },
    // Today's date in yyyy-mm-dd, used to cap the date range inputs
    maxDate() {
      return new Date().toISOString().slice(0, 10);
    },
    // Computed property for filtered reports based on search/date filters
    filteredReports() {
      if (!this.filtersApplied) {
        return this.previousReports;
      }
      return this.searchResults;
    },
    combinedMetrics() {
      const desktop = this.parsedReport.find(r => r.formFactor === "Desktop");
      const mobile = this.parsedReport.find(r => r.formFactor === "Mobile");
      if (!desktop || !mobile) return [];
      return desktop.metrics.map((metric, i) => ({
        title: metric.title,
        type: metric.type,
        description: metric.description,
        desktop: metric,
        mobile: mobile.metrics[i]
      }));
    },
    hasWpoData() {
      return this.wpoData && this.wpoData.success && this.wpoData.wpo_data.domains.length > 0;
    },
    wpoDomain() {
      return this.hasWpoData ? this.wpoData.wpo_data.domains[0].domain : null;
    },
    wpoIP() {
      return this.hasWpoData ? this.wpoData.wpo_data.domains[0].ip : null;
    },
    phpVer() {
      return this.hasWpoData ? this.wpoData.wpo_data.domains[0].phpVer : null;
    },
    wpVer() {
      return this.hasWpoData ? this.wpoData.wpo_data.domains[0].wpVer : null;
    },
    plugins() {
      if (!this.hasWpoData) return [];
      return this.wpoData.wpo_data.domains[0].plugins
    },
    safePlugins() {
      if (!this.hasWpoData) return [];
      return this.wpoData.wpo_data.domains[0].plugins.filter(
          (p) => (p.security === 'Safe' && p.name)
      );
    },
    vulnerablePlugins() {
      if (!this.hasWpoData) return [];
      return this.wpoData.wpo_data.domains[0].plugins.filter(
          (p) => Array.isArray(p.security)
      );
    },
    themes() {
      if (!this.hasWpoData) return [];
      return this.wpoData.wpo_data.domains[0].themes || [];
    }
  },
  methods: {
    async generateLink() {
      $(document).ready(function() {
        $(".modal-backdrop").appendTo(".link-share-modal");
      });

      if (!this.docId) {
        swal('Error', 'Please run the audit first to generate a link.', 'error');
        return;
      }
      const encodedId = crypto.AES.encrypt(this.docId, this.aesSecret).toString().split('/').join('Por21Ld') .split('+').join('xMl3Jk') .split('=').join('Ml32') //btoa();
      this.generatedLink = `https://wpo.bigscoots.com/report/performance-audit?reportId=${encodedId}`;
      this.showLinkModal = true;
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.generatedLink);
        this.doCopy(this.generatedLink)
        // swal('Success', 'Link copied to clipboard!', 'success');
      } catch (err) {
        console.error('Failed to copy link:', err);
        swal('Error', 'Failed to copy link to clipboard', 'error');
      }
    },

    getCruxMetric(metric, formFactor = 'mobile') {
      const report = this.parsedReport.find(r => r.formFactor === (formFactor === 'desktop' ? 'Desktop' : 'Mobile'));
      if (!report) return { p75: 'N/A', description: '', note: '' };

      const fallback = { p75: 'N/A', description: '', note: '' };

      switch (metric) {
        case 'lcp':
          const lcpMetric = report.metrics.find(m => m.type === 'lcp');
          return {
            p75: lcpMetric ? lcpMetric.p75 : 'N/A',
            description: 'Largest Contentful Paint',
            note: `Measures loading performance.<br> Target: < 2.5s`
          };
        case 'inp':
          const inpMetric = report.metrics.find(m => m.type === 'inp');
          return {
            p75: inpMetric ? inpMetric.p75 : 'N/A',
            description: 'Interaction to Next Paint',
            note: `Measures interactivity.<br> Target: < 200ms`
          };
        case 'cls':
          const clsMetric = report.metrics.find(m => m.type === 'cls');
          return {
            p75: clsMetric ? clsMetric.p75 : 'N/A',
            description: 'Cumulative Layout Shift',
            note: `Measures visual stability.<br> Target: < 0.1`
          };
        case 'ttfb':
          if (this.crux && this.crux.data && this.crux.data[formFactor]) {
            const metrics = this.crux.data[formFactor].full.metrics;
            const ttfbMetric = metrics && metrics.experimental_time_to_first_byte;
            const ttfb = (ttfbMetric && ttfbMetric.percentiles && ttfbMetric.percentiles.p75) || 0;
            return {
              p75: ttfb > 0 ? (ttfb / 1000).toFixed(1) + 's' : 'N/A',
            };
          } else {
            return fallback;
          }
        case 'fcp':
          if (this.crux && this.crux.data && this.crux.data[formFactor]) {
            const metrics = this.crux.data[formFactor].full.metrics;
            const fcpMetric = metrics && metrics.first_contentful_paint;
            const fcp = (fcpMetric && fcpMetric.percentiles && fcpMetric.percentiles.p75) || 0;
            return {
              p75: fcp > 0 ? (fcp / 1000).toFixed(1) + 's' : 'N/A',
            };
          } else {
            return fallback;
          }
        default:
          return fallback;
      }
    },
    getCruxBadge(metric, formFactor = 'mobile') {
      const val = this.getCruxMetric(metric, formFactor).p75;
      if (val === 'N/A') return { level: 'N/A' };
      
      // Extract numeric value (handle cases like "2.5s" or "200ms")
      const numericStr = val.toString().replace(/[^\d.]/g, '');
      const numeric = parseFloat(numericStr);
      
      if (isNaN(numeric)) return { level: 'N/A' };
      
      switch (metric) {
        case 'lcp':
          if (numeric < 2.5) return { level: 'Good' };
          if (numeric < 4.0) return { level: 'Needs Improvement' };
          return { level: 'Poor' };
        case 'inp':
          if (numeric < 200) return { level: 'Good' };
          if (numeric < 500) return { level: 'Needs Improvement' };
          return { level: 'Poor' };
        case 'cls':
          if (numeric < 0.1) return { level: 'Good' };
          if (numeric < 0.25) return { level: 'Needs Improvement' };
          return { level: 'Poor' };
        default:
          return { level: 'N/A' };
      }
    },
    getMetricColorClass(metric, formFactor, goodThreshold, warningThreshold) {
      const val = this.getCruxMetric(metric, formFactor).p75;
      if (val === 'N/A') return 'text-muted';
      
      // Extract numeric value (handle cases like "2.5s" or "200ms")
      const numericStr = val.toString().replace(/[^\d.]/g, '');
      const numeric = parseFloat(numericStr);
      
      if (isNaN(numeric)) return 'text-muted';
      
      if (numeric < goodThreshold) return 'text-success';
      if (numeric < warningThreshold) return 'text-warning';
      return 'text-danger';
    },
    getMetricTitle(metric) {
      const titles = {
        'lcp': 'Largest Contentful Paint (LCP)',
        'inp': 'Interaction to Next Paint (INP)',
        'cls': 'Cumulative Layout Shift (CLS)'
      };
      return titles[metric] || metric.toUpperCase();
    },
    getDataCollectionPeriod() {
      // Calculate date range (typically 28 days for CrUX data)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 28);
      
      const formatDate = (date) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const daySuffix = day === 1 || day === 21 || day === 31 ? 'st' :
                         day === 2 || day === 22 ? 'nd' :
                         day === 3 || day === 23 ? 'rd' : 'th';
        return `${month} ${day}${daySuffix}, ${year}`;
      };
      
      return `${formatDate(startDate)} through ${formatDate(endDate)}`;
    },
    async submitKetFindings () {
      try {
        this.uodatedKeyFindings = this.keyFindings
        await db.collection('performance-report').doc(this.docId).update({
          keyFindings: this.keyFindings,
          keyFindingsUpdatedBy: this.user.firstName + ' ' + this.user.lastName,
          keyFindingsUpdatedByEmailHash: this.userEmailHash,
          modified: new Date().toISOString()
        })
      } catch (e) {
        console.log('Error in saving key findings', e.toString())
      } finally {
        $('#newKeyFindingsModal').modal('hide');
      }
    },
    fixModal() {
      /* eslint-disable */
      $(document).ready(function() {
        $(".modal-backdrop").appendTo(".new-findings-modal");
      });
      /* eslint-enable */
    },
    dateFormat (date) {
      return moment(date).format("MMM Do, YYYY")
    },
    getProgressStyle(score) {
      const angle = (score / 100) * 360
      return `conic-gradient(#28a745 ${angle}deg, #e9ecef 0deg)`
    },
    openNotesModal (type) {
      this.$refs.notes.load(this.comments,type, this.docId)
    },
    openPluginCommentModal (plugin) {
      this.$refs.pluginCommentModal.load(plugin, this.docId, this.wpoData)
    },
    openThemeCommentModal (theme) {
      this.$refs.pluginCommentModal.load(theme, this.docId, this.wpoData)
    },
    stripHtml(html) {
      if (!html) return '';
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    onPluginCommentUpdated(updatedWpoData) {
      // Update the local wpoData when modal saves changes
      this.wpoData = updatedWpoData;
    },
    async downloadPdf() {
      try {
        if (!this.docId) {
          swal('Error', 'Please run the audit first to generate a PDF.', 'error');
          return;
        }

        // Show loading state
        const downloadBtn = document.querySelector('.download-pdf-btn');
        const originalText = downloadBtn.querySelector('span:last-child').textContent;
        downloadBtn.querySelector('span:last-child').textContent = 'Generating PDF...';
        downloadBtn.disabled = true;

        const encodedId = crypto.AES.encrypt(this.docId, this.aesSecret).toString().split('/').join('Por21Ld') .split('+').join('xMl3Jk') .split('=').join('Ml32')
        const response = await this.$http.post('/utilities/generate-audit-pdf', {
          grant_type: 'token',
          reportId: encodedId,
        }, {
          responseType: 'blob', // Important: Set response type to blob for binary data
          timeout: 300000 // 5 minutes timeout to match backend
        });


        // Check if response is actually a blob and has content
        if (!response.data || response.data.size === 0) {
          throw new Error('Empty PDF response from server');
        }

        // Check if response is an error JSON instead of PDF
        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          throw new Error('Server returned error instead of PDF: ' + text);
        }

        // Try alternative approach: Use the response data directly
        let blob;
        
        // Method 1: Try using response.data directly if it's already a blob
        if (response.data instanceof Blob) {
          console.log('Using response.data as blob directly');
          blob = response.data;
        } else {
          console.log('Creating new blob from response.data');
          blob = new Blob([response.data], { type: 'application/pdf' });
        }
        
        // Check if blob has PDF signature
        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const pdfSignature = String.fromCharCode(...uint8Array.slice(0, 4));

        if (pdfSignature !== '%PDF') {
          console.error('Invalid PDF signature. First 20 bytes:', String.fromCharCode(...uint8Array.slice(0, 20)));
          throw new Error('Invalid PDF file received from server');
        }
        
        // Create blob URL
        const url = window.URL.createObjectURL(blob);

        // Alternative download method using fetch API
        try {
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = url;
          link.download = `performance-report-${new Date().getTime()}.pdf`;
          
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 1000);
          
        } catch (downloadError) {
          console.error('Download failed, trying alternative method:', downloadError);
          
          // Alternative: Open in new window
          window.open(url, '_blank');
        }
        
        // Reset button state
        downloadBtn.querySelector('span:last-child').textContent = originalText;
        downloadBtn.disabled = false;

        swal('Success', 'PDF downloaded successfully!', 'success');
        
      } catch (error) {
        console.error('Error downloading PDF:', error);
        
        // Reset button state
        const downloadBtn = document.querySelector('.download-pdf-btn');
        if (downloadBtn) {
          downloadBtn.querySelector('span:last-child').textContent = 'Download Report as PDF';
          downloadBtn.disabled = false;
        }

        let errorMessage = 'Failed to generate PDF. Please try again.';
        
        if (error.response) {
          if (error.response.status === 504) {
            errorMessage = 'PDF generation timed out. The report may be too large or complex.';
          } else if (error.response.status === 400) {
            errorMessage = 'Invalid report ID. Please run the audit again.';
          } else if (error.response.status === 500) {
            errorMessage = 'Server error while generating PDF. Please contact support.';
          }
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Request timed out. Please try again.';
        }
        
        swal('Error', errorMessage, 'error');
      }
    },
    async submitAudit() {
      // Reset form state
      this.parsedReport = [];
      this.gtmetrixData = null;
      this.siteUrl = null;
      this.wpoData = null;
      this.crux = null;
      this.docId = null;
      this.generatedLink = null;
      this.uodatedKeyFindings = '';
      this.keyFindings = '';
      this.comments = {
        lcp: [],
        cls: [],
        inp: [],
        gtmatrics: [],
        plugins: [],
        pluginlist: [],
        themelist: []
      };
      // Clear search when resetting form
      this.clearFilters();

      this.loading = true;
      this.parsedReport = [];
      this.gtmetrixData = null;
      this.siteUrl = null;
      this.wpoData = null;
      this.crux = null;
      // Reset report metadata for new audit
      this.reportAuthor = null;
      this.reportAuthorEmailHash = null;
      this.reportCreatedDate = null;
      this.urlInput = this.urlInput.trim()
      const domainForSearch = this.normalizeDomainForSearch(this.urlInput)
      try {
        let query = await db.collection('domains').where('domain', '==', domainForSearch).get();
        
        // Handle multiple domains
        if (query.size > 1) {
          this.multipleDomains = query.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          this.selectedDomainIndex = null;
          this.showDomainSelectionModal = true;
          this.loading = false;
          $('#domainSelectionModal').modal('show');
          $('.modal-backdrop').removeClass("modal-backdrop");
          return;
        }
        
        // Handle single or no domain
        this.domainData = query.size ? query.docs[0].data() : {}
        await this.continueWithDomainData();
      } catch (err) {
        console.error(err);
      } finally {
        if (!this.showDomainSelectionModal) {
          this.loading = false;
          // Ensure we stay on the new audit tab after running an audit
          this.activeTab = 'new-audit';
          
          // Smooth scroll to report content after audit completion
          if (this.parsedReport.length || this.gtmetrixData || this.wpoDomain) {
            this.smoothScrollTo('reportContent', 100);
          }
        }
      }
    },

    async continueWithDomainData() {
      try {
        const { ip, domain } = this.domainData;
        if (ip) {
          try {
            let response = await this.$http.post('/utilities/get-canonical-url-redirect', {
              grant_type: 'token',
              ip: ip,
              domain: domain
            });
            console.log('response', response.data);

            response= JSON.parse(response.data)

            if (response.success) {
              const nestedPath = this.extractNestedPath(this.urlInput);
              this.siteUrl = this.appendPathToCanonicalUrl(response.siteurl, nestedPath);
            }
          } catch (e) {
            // this.loading = false;
            console.log(e.toString());
            this.loading = false;
            return;
          }
        } else {
          this.siteUrl = this.urlInput
        }
        
        if (this.siteUrl) {
          if (ip && domain) {
            try {
              await this.$patchStackService.post('/patchstack/sync_plugins', {
                grant_type: 'token',
                ip,
                domain,
                syncAll: false,
                planIndex: this.domainData.planIndex,
                uid: this.domainData.uid
              });
            } catch (syncErr) {
              console.error('Patchstack sync_plugins failed:', syncErr);
            }
          }
          let response = await this.$http.post('/utilities/generate-performance-report', {
            grant_type: 'token',
              url: this.siteUrl,
              adblock: this.adblockChecked
          });
          // const response = await fetch("https://n8n.bigscoots.dev/webhook/f7f39f47-1b40-48f4-abbc-37bf2791b0cf", {
          //   method: "POST",
          //   headers: {
          //     "Content-Type": "application/json",
          //     "Authorization": "Bearer QlkComRiKHyY9cRerxj4c7UYM4Rle53SdSBO9mzf"
          //   },
          //   body: JSON.stringify({
          //     url: this.siteUrl,
          //     adblock: this.adblockChecked
          //   })
          // });
          // const result = await response.json();
          const result = await response.data.data
          console.log('result',result)
          this.saveInDB(result)

          // Parse CrUX
          this.crux = result.find(obj => obj.type === "crux");
          if (this.crux && this.crux.data) {
            for (const factor of ["desktop", "mobile"]) {
              if (this.crux.data[factor]) {
                this.parsedReport.push({
                  formFactor: factor === "desktop" ? "Desktop" : "Mobile",
                  metrics: this.parseMetrics(this.crux.data[factor].full.metrics)
                });
              }
            }
          }
          console.log('this.parsedReport', this.parsedReport)
          // Parse GTmetrix
          const gtmetrix = result.find(obj => obj.type === "gtmetrix");
          this.wpoData = result.find(obj => obj.type === "wpo");
          if (gtmetrix) {
            this.gtmetrixData = gtmetrix.data;
            console.log('this.gtmetrixData', this.gtmetrixData)
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
        // Ensure we stay on the new audit tab after running an audit
        this.activeTab = 'new-audit';
        
        // Smooth scroll to report content after audit completion
        if (this.parsedReport.length || this.gtmetrixData || this.wpoDomain) {
          this.smoothScrollTo('reportContent', 100);
        }
      }
    },
    computeGrade(score) {
      if (score >= 90) return 'A';
      if (score >= 80) return 'B';
      if (score >= 70) return 'C';
      if (score >= 60) return 'D';
      return 'F';
    },
    /** Hostname only for Firestore domain lookup; strips scheme/path and leading www. */
    normalizeDomainForSearch(input) {
      const host = this.cleanDomain(input);
      if (!host) return '';
      return host.replace(/^www\./i, '');
    },
    /** Path, query, and hash from user input (e.g. domain.com/home → /home). */
    extractNestedPath(input) {
      if (!input) return '';
      let normalized = input.trim();
      if (!/^https?:\/\//i.test(normalized)) {
        normalized = 'http://' + normalized;
      }
      try {
        const urlObj = new URL(normalized);
        const path = urlObj.pathname === '/' ? '' : urlObj.pathname;
        return path + urlObj.search + urlObj.hash;
      } catch (e) {
        const withoutScheme = input.trim().replace(/^https?:\/\//i, '');
        const slashIndex = withoutScheme.indexOf('/');
        if (slashIndex === -1) return '';
        return withoutScheme.slice(slashIndex);
      }
    },
    appendPathToCanonicalUrl(canonicalUrl, nestedPath) {
      if (!nestedPath) return canonicalUrl;
      if (!canonicalUrl) return nestedPath;
      const base = canonicalUrl.replace(/\/+$/, '');
      const path = nestedPath.startsWith('/') ? nestedPath : '/' + nestedPath;
      return base + path;
    },
    cleanDomain(url) {
      if (!url) return '';
      // Remove http:// or https://
      let cleaned = url.replace(/^https?:\/\//i, '');
      // Remove trailing slashes
      cleaned = cleaned.replace(/\/+$/, '');
      // Extract just the domain name (remove path, query params, etc.)
      try {
        const urlObj = new URL('http://' + cleaned);
        return urlObj.hostname;
      } catch (e) {
        // If URL parsing fails, return cleaned string without path
        return cleaned.split('/')[0].split('?')[0].split('#')[0];
      }
    },
    async saveInDB(resp) {
      try {
        const ref = await db.collection('performance-report').add({
          reportData: resp,
          siteUrl: this.siteUrl,
          addedByEmailHash: this.userEmailHash,
          addedBy: `${this.user.firstName} ${this.user.lastName}`,
          uid: this.domainData.uid || '',
          domain: this.cleanDomain(this.urlInput),
          ip: this.domainData.ip || '',
          comments: this.comments,
          createdAt: new Date().toISOString()
        });
        this.docId = ref.id
        await this.generateLink()
      } catch (e) {
        console.log('Error in adding report in DB')
      }
    },
    parseMetrics(metrics) {
      return [
        {
          title: "Largest Contentful Paint (LCP)",
          type: 'lcp',
          description: "Measures loading performance. LCP marks the time at which the largest text or image is painted.",
          histogram: metrics.largest_contentful_paint ? metrics.largest_contentful_paint.histogram : [],
          p75: metrics.largest_contentful_paint ? (Number(metrics.largest_contentful_paint.percentiles.p75) / 1000).toFixed(1) + "s" : 'N/A'
        },
        {
          title: "Cumulative Layout Shift (CLS)",
          type: 'cls',
          description: "Measures visual stability. CLS quantifies how often users experience unexpected layout shifts",
          histogram: metrics.cumulative_layout_shift ? metrics.cumulative_layout_shift.histogram : [],
          p75: metrics.cumulative_layout_shift ? metrics.cumulative_layout_shift.percentiles.p75 : 'N/A'
        },
        {
          title: "Interaction to Next Paint (INP)",
          type: 'inp',
          description: "Measures responsiveness. INP measures the time from when a user interacts with the page to when the next frame is painted",
          histogram: metrics.interaction_to_next_paint ? metrics.interaction_to_next_paint.histogram : [],
          p75: metrics.interaction_to_next_paint ? metrics.interaction_to_next_paint.percentiles.p75 + "ms" : 'N/A'
        }
      ];
    },
    getBarClass(index) {
      return ["bg-success", "bg-warning text-dark", "bg-danger"][index] || "bg-secondary";
    },

    getVulnerabilityClass(val){
      let clas = '';
      if(val === 'Safe') {
        clas = 'text-green-700 bg-green-100'
      } else {
        clas = 'text-red-700 bg-red-100'
      }
      return clas
    },
    getThemeSecurityClass(security) {
      if (!security || security === undefined || security === null) {
        return 'text-muted bg-light'
      }
      if (security === 'Safe') {
        return 'text-green-700 bg-green-100'
      }
      if (Array.isArray(security)) {
        return 'text-red-700 bg-red-100'
      }
      return 'text-muted bg-light'
    },
    getThemeSecurityDisplay(security) {
      if (!security || security === undefined || security === null) {
        return 'N/A'
      }
      if (Array.isArray(security)) {
        return 'vulnerable'
      }
      return security
    },
    getThemeStatusClass(status) {
      if (status === 'active') {
        return 'text-green-700 bg-green-100'
      } else if (status === 'parent') {
        return 'text-blue-700 bg-blue-100'
      }
      return 'text-red-700 bg-red-100'
    },
    getThemeStatusDisplay(status) {
      if (status === 'active') {
        return 'Active'
      } else if (status === 'parent') {
        return 'Parent'
      }
      return 'Inactive'
    },

    // New methods for tab interface and previous reports functionality
    switchTab(tabName) {
      this.activeTab = tabName;
      
      // Load previous reports when switching to that tab
      if (tabName === 'previous-reports' && this.previousReports.length === 0) {
        this.loadPreviousReports();
      }
    },

    async loadPreviousReports() {
      this.loadingReports = true;
      try {
        // Try to order by createdAt first, fallback to no ordering if index doesn't exist
        let query;
        try {
          // createdAt is a mix of ISO strings (current format) and legacy pre-formatted
          // strings like "Aug 8th, 2025" saved by older versions of this page. Those legacy
          // strings sort above any ISO string in a plain desc order (letters > digits in a
          // string compare), which surfaces old reports as "latest". Restricting the range to
          // ISO-shaped (digit-prefixed) values keeps the sort limited to properly dated reports.
          query = await db.collection('performance-report')
            .where('createdAt', '>=', '1000-01-01')
            .where('createdAt', '<=', '9999-12-31T23:59:59.999Z')
            .orderBy('createdAt', 'desc')
            .limit(10)
            .get();
        } catch (indexError) {
          console.log('Using fallback query without ordering');
          // Fallback to no ordering if index doesn't exist
          query = await db.collection('performance-report')
            .limit(10)
            .get();
        }
        
        this.previousReports = query.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
      } catch (error) {
        console.error('Error loading previous reports:', error);
        swal('Error', 'Failed to load previous reports. Please try again.', 'error');
      } finally {
        this.loadingReports = false;
      }
    },

    resetFormState() {
      // Reset form state
      this.urlInput = "";
      this.adblockChecked = false;
      this.parsedReport = [];
      this.gtmetrixData = null;
      this.siteUrl = null;
      this.wpoData = null;
      this.crux = null;
      this.docId = null;
      this.generatedLink = null;
      this.uodatedKeyFindings = '';
      this.keyFindings = '';
      this.comments = {
        lcp: [],
        cls: [],
        inp: [],
        gtmatrics: [],
        plugins: [],
        pluginlist: [],
        themelist: []
      };
      // Reset report metadata to show current user/date for new reports
      this.reportAuthor = null;
      this.reportAuthorEmailHash = null;
      this.reportCreatedDate = null;
      // Clear search when resetting form
      this.clearFilters();

      // Reset domain selection state
      this.resetDomainSelection();
    },

    async loadReport(report) {
      try {
        // Reset form state first
        this.resetFormState();
        
        // Set the report data to display
        this.docId = report.id;
        this.siteUrl = report.siteUrl;
        this.urlInput = report.domain || '';
        this.uodatedKeyFindings = report.keyFindings || '';
        this.keyFindings = report.keyFindings || '';
        this.comments = report.comments ? {...this.comments, ...report.comments} : {
          lcp: [],
          cls: [],
          inp: [],
          gtmatrics: [],
          plugins: [],
          pluginlist: [],
          themelist: []
        };

        // Set the original report metadata
        this.reportAuthor = report.addedBy || 'Unknown';
        this.reportAuthorEmailHash = report.addedByEmailHash || '';
        this.reportCreatedDate = report.createdAt || this.dateFormat(new Date());

        // Parse the report data
        if (report.reportData && Array.isArray(report.reportData)) {
          const result = report.reportData;
          
          // Parse CrUX
          this.crux = result.find(obj => obj.type === "crux");
          if (this.crux && this.crux.data) {
            this.parsedReport = [];
            for (const factor of ["desktop", "mobile"]) {
              if (this.crux.data[factor]) {
                this.parsedReport.push({
                  formFactor: factor === "desktop" ? "Desktop" : "Mobile",
                  metrics: this.parseMetrics(this.crux.data[factor].full.metrics)
                });
              }
            }
          }
          
          // Parse GTmetrix
          const gtmetrix = result.find(obj => obj.type === "gtmetrix");
          this.wpoData = result.find(obj => obj.type === "wpo");
          if (gtmetrix) {
            this.gtmetrixData = gtmetrix.data;
          }
        }

        // Generate shareable link
        await this.generateLink();
        
        // Keep the user on the current tab (don't auto-switch to new-audit)
        
        // Smooth scroll to report content after loading
        this.smoothScrollTo('reportContent', 100);
        
      } catch (error) {
        console.error('Error loading report:', error);
        swal('Error', 'Failed to load the report. Please try again.', 'error');
      }
    },

    async deleteReport(report) {
      swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete the report for ${report.siteUrl || report.domain}? This cannot be undone.`,
        type: "question",
        confirmButtonText: "Yes, Delete",
        confirmButtonColor: "#dc3545",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        cancelButtonColor: "#6c757d"
      }).then(async (result) => {
        if (!result.value) return;

        this.deletingReportId = report.id;
        try {
          await db.collection('performance-report').doc(report.id).delete();

          this.previousReports = this.previousReports.filter(r => r.id !== report.id);
          this.searchResults = this.searchResults.filter(r => r.id !== report.id);

          // If the report currently on screen was the one deleted, clear it out
          if (this.docId === report.id) {
            this.resetFormState();
          }

          swal('Deleted!', 'The report has been deleted.', 'success');
        } catch (error) {
          console.error('Error deleting report:', error);
          swal('Error', 'Failed to delete the report. Please try again.', 'error');
        } finally {
          this.deletingReportId = null;
        }
      });
    },

    // Search functionality methods
    // Strip protocol/www and normalize casing so "https://www.Vape.co.uk" and "vape" both match
    normalizeSearchTerm(term) {
      if (!term) return '';
      return term
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '');
    },

    isWithinDateRange(createdAt) {
      if (!createdAt) return false;
      const time = new Date(createdAt).getTime();
      if (this.dateFrom && time < new Date(`${this.dateFrom}T00:00:00`).getTime()) {
        return false;
      }
      if (this.dateTo && time > new Date(`${this.dateTo}T23:59:59.999`).getTime()) {
        return false;
      }
      return true;
    },

    // Debounce so a search fires automatically as the user types, without
    // sending a Firestore query on every single keystroke
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 350);
    },

    // Runs the search term / date range filters, combining server-side Firestore
    // queries (indexed range reads, no full-collection scans) with a light client-side
    // pass only when both a term and a date range are active at once.
    // Triggered automatically while typing (debounced), on Enter, or via the Search button.
    async applyFilters() {
      const term = this.normalizeSearchTerm(this.searchTerm);
      const hasDateRange = !!(this.dateFrom || this.dateTo);

      if (!term && !hasDateRange) {
        this.clearFilters();
        return;
      }

      // Snapshot what's actually being queried so result messaging stays accurate
      // even if the user keeps typing before clicking Search again
      this.appliedSearchTerm = this.searchTerm;
      this.appliedDateFrom = this.dateFrom;
      this.appliedDateTo = this.dateTo;
      this.filtersApplied = true;

      this.isSearching = true;
      try {
        let results;

        if (term && term.length < 2) {
          // Too short for an efficient prefix query - filter within the already-loaded reports
          results = this.previousReports.filter(report => this.matchesTerm(report, term));
        } else if (term) {
          // Prefix range queries on 'domain' and 'siteUrl' run in parallel (indexed, no scan)
          const [domainSnap, urlSnap] = await Promise.all([
            db.collection('performance-report')
              .where('domain', '>=', term)
              .where('domain', '<=', term + '\uf8ff')
              .limit(20)
              .get(),
            db.collection('performance-report')
              .where('siteUrl', '>=', term)
              .where('siteUrl', '<=', term + '\uf8ff')
              .limit(20)
              .get()
          ]);

          const merged = new Map();
          domainSnap.docs.forEach(doc => merged.set(doc.id, { id: doc.id, ...doc.data() }));
          urlSnap.docs.forEach(doc => merged.set(doc.id, { id: doc.id, ...doc.data() }));
          results = Array.from(merged.values());

          if (results.length === 0) {
            // Fallback to a contains-style match on the reports already in memory
            results = this.previousReports.filter(report => this.matchesTerm(report, term));
          }
        } else {
          // Date range only - createdAt is stored as an ISO string, so a range query is sortable server-side
          let query = db.collection('performance-report');
          if (this.dateFrom) {
            query = query.where('createdAt', '>=', new Date(`${this.dateFrom}T00:00:00`).toISOString());
          }
          if (this.dateTo) {
            query = query.where('createdAt', '<=', new Date(`${this.dateTo}T23:59:59.999`).toISOString());
          }
          const snap = await query.orderBy('createdAt', 'desc').limit(100).get();
          results = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // Firestore can't range-filter two different fields in one query, so when both
        // a search term and a date range are active, narrow the (already small) result set here
        if (term && hasDateRange) {
          results = results.filter(report => this.isWithinDateRange(report.createdAt));
        }

        results.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        this.searchResults = results;
      } catch (error) {
        console.error('Error applying filters:', error);
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },

    matchesTerm(report, term) {
      const haystack = `${report.domain || ''} ${report.siteUrl || ''}`.toLowerCase();
      return haystack.includes(term);
    },

    clearFilters() {
      this.searchTerm = '';
      this.dateFrom = '';
      this.dateTo = '';
      this.appliedSearchTerm = '';
      this.appliedDateFrom = '';
      this.appliedDateTo = '';
      this.filtersApplied = false;
      this.searchResults = [];
      this.isSearching = false;
    },

    // Smooth scrolling utility method
    smoothScrollTo(elementId, offset = 0) {
      // this.$nextTick(() => {
      //   const element = document.getElementById(elementId);
      //   if (element) {
      //     const elementPosition = element.offsetTop - offset;
      //     window.scrollTo({
      //       top: elementPosition,
      //       behavior: 'smooth'
      //     });
      //   }
      // });
      const element = document.getElementById(elementId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 20; // 20px offset from top

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    },

    // Domain selection methods
    selectDomain(index) {
      this.selectedDomainIndex = index;
    },

    async proceedWithSelectedDomain() {
      if (this.selectedDomainIndex === null) {
        swal('Error', 'Please select a domain to continue.', 'error');
        return;
      }

      // Set the selected domain data
      this.domainData = this.multipleDomains[this.selectedDomainIndex];
      console.log('this.domainData', this.domainData)
      // Hide the modal
      $('#domainSelectionModal').modal('hide');
      this.showDomainSelectionModal = false;
      
      // Reset loading state and continue with audit
      this.loading = true;
      
      try {
        await this.continueWithDomainData();
      } catch (error) {
        console.error('Error continuing with selected domain:', error);
        swal('Error', 'Failed to continue with the selected domain. Please try again.', 'error');
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const parsed = moment(dateString);
      if (parsed.isValid()) {
        return parsed.format('MMM DD, YYYY');
      }
      // Older reports stored createdAt as an already-formatted string (e.g. "Aug 8th, 2025")
      // rather than an ISO date, which moment can't re-parse - fall back to showing it as-is
      return dateString;
    },

    // Reset domain selection state
    resetDomainSelection() {
      this.multipleDomains = [];
      this.selectedDomainIndex = null;
      this.showDomainSelectionModal = false;
    },
  }
};
</script>

<style scoped>
.progress-bar {
  transition: width 0.6s ease;
}

.flex-column {
  display: flex;
  flex-direction: column;
}
#reportContent {
  background: #fff;
}

.bg-success {
  background-color: #08a14c !important;
}
.bg-warning {
  color: black !important;
  background-color: rgb(244 200 108) !important;
}
.badge.bg-warning {
  border: none;
}
.alert.alert-warning {
  color: black !important;
  border: none;
  background-color: rgb(244 200 108) !important;
}
.alert.alert-danger {
  color: black !important;
  border: none;
  background-color: rgb(244 200 108) !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}
.circular-progress {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circular-inner {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.score-text {
  font-size: 1rem;
  color: #343a40;
}
.text-dark-gray {
  color: #343a40;
}
body {
  background-color: #f8f9fa; /* Light background similar to the image */
  font-family: 'Poppins', sans-serif; /* Optional: Use Poppins for a similar font style */
}

.bg-light-blue {
  background-color: #f0f4f8; /* A light blue background for the header */
}

.card {
  border-radius: 10px; /* Rounded corners for the card */
}

.card-title {
  font-weight: 600; /* Slightly bolder card titles */
}


.fa { /* For Font Awesome icons */
  font-size: 1.2em; /* Adjust icon size */
}
.main-page {
  background-color: #f8f9fa;
  color: #212529;
}
.prose {
  color: #495057;
}
.prose p {
  margin-bottom: 1em;
}
ul {
  margin-left: 0 !important;
  list-style-type: none;
  padding-left: 0;
}
li {
  margin-top: 5px;
}
.prose ul {
  list-style-position: inside;
  padding-left: 0;
}
.prose li {
  margin-bottom: 0.5em;
}
.metric-circle {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-circle-compact {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.metric-circle-compact:hover {
  transform: scale(1.05);
}

.metric-circle-large {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.metric-circle-large:hover {
  transform: scale(1.05);
}

.metric-value-large {
  font-size: 1.5rem;
  line-height: 1.2;
}

.device-label {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

.core-vital-card {
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
}

.core-vital-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.additional-metric-card {
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
}

.additional-metric-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.data-collection-periods {
  background-color: #f0f4f8;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.data-collection-title {
  color: #24292f;
  font-size: 0.95rem;
  font-weight: 600;
}

.data-collection-content {
  padding-left: 1.5rem;
}

.origin-data-highlight {
  color: #ff6b35;
  font-weight: 500;
}
.display-5 {
  font-size: 2.2em;
}
.progress-circle {
  position: relative;
  width: 96px;
  height: 96px;
}
.progress-circle svg {
  width: 100%;
  height: 100%;
}
.progress-circle span {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
}

.print-only .progress-circle {
  position: relative;
  width: 120px;
  height: 120px;
  display: inline-block;
}
.print-only .progress-circle svg {
  width: 100%;
  height: 100%;
  display: block;
}
.print-only .progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.2rem;
  font-weight: bold;
  color: #343a40;
  z-index: 10;
}
.card-section {
  width: 100%;
  background-color: white;
  border: 1px solid #dee2e6;
  /* border-radius: 0.375rem; */
  /* box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); */
}
.bg-opacity-10 { opacity: 0.5; }
.bg-success-10 { background-color: rgba(38, 250, 86, 0.5); }
.bg-warning-10 { background-color: rgba(255, 193, 7, 0.1); }
.bg-danger-10 { background-color: rgba(220, 53, 69, 0.1); }

.bg-yellow-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(254 249 195 / var(--tw-bg-opacity, 1));
}
.text-yellow-700 {
  --tw-text-opacity: 1;
  color: rgb(161 98 7 / var(--tw-text-opacity, 1));
}
.bg-blue-100 {
  --tw-bg-opacity: 1;
  background-color: rgba(59, 130, 246, 0.2);
}
.text-blue-700 {
  --tw-text-opacity: 1;
  color: rgb(29 78 216 / var(--tw-text-opacity, 1));
}
.bg-green-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(220 252 231 / var(--tw-bg-opacity, 1));
}
.text-green-700 {
  --tw-text-opacity: 1;
  color: rgb(21 128 61 / var(--tw-text-opacity, 1));
}
.bg-red-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(255, 0, 0, 0.3);
}
.text-red-700 {
  --tw-text-opacity: 1;
  color: rgb(204 0 0 / var(--tw-text-opacity, 1));
}

/* Standardized Report Styles */
/* Base text styles */
[class^="report-"] {
  color: #343a40;
  margin-bottom: 0.5rem;
}

/* Text sizes and weights */
.report-main-heading {
  font-size: 1.75rem;
  font-weight: bold;
  margin: 0;
}

.report-section-heading {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.report-sub-heading {
  font-size: 1.25rem;
  font-weight: 500;
}

.report-text,
.report-text-small {
  color: #6c757d;
  font-weight: 500;
}

.report-text {
  font-size: 1rem;
}

.report-text-small {
  font-size: 0.75rem;
}

.print-only {
  display: none !important;
}
@media print {
  .no-print {
    display: none !important;
  }
  .print-only {
    display: block !important;
  }
  .pdf-break-before {
    margin-top: 45px;
    padding-top: 5px;
  }
}

.share-link-card {
  background: linear-gradient(90deg, #f5f8ff 0%, #eaf0fb 100%);
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(31, 75, 241, 0.07);
}

.share-link-icon {
  width: 54px;
  height: 54px;
  background: #1f4bf1;
  color: #fff;
  border-radius: 50%;
  font-size: 1.7rem;
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.12);
}

.btn-brand.download-pdf-btn {
  background: linear-gradient(90deg, #1f4bf1 0%, #3b82f6 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.12);
  font-size: 0.8rem;
  transition: background 0.2s, box-shadow 0.2s;
  font-weight: 400;
}
.btn-brand.download-pdf-btn:hover, .btn-brand.download-pdf-btn:focus {
  background: linear-gradient(90deg, #3b82f6 0%, #1f4bf1 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(31, 75, 241, 0.18);
}
.pdf-icon-wrapper {
  background: #fff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.10);
  margin-right: 10px;
}
.share-link-card {
  background: linear-gradient(90deg, #f5f8ff 0%, #eaf0fb 100%);
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(31, 75, 241, 0.07);
  min-width: 260px;
}
.share-link-icon {
  width: 54px;
  height: 54px;
  background: #1f4bf1;
  color: #fff;
  border-radius: 50%;
  font-size: 1.7rem;
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.12);
}

/* Previous Reports List Styles */
.reports-list {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
}

.report-item {
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.report-item:hover {
  background-color: #f8f9fa;
  border-color: #1f4bf1 !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.1);
}

.report-item:last-child {
  margin-bottom: 0 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.gap-2 {
  gap: 0.5rem;
}

/* Tab Styles */
.nav-tabs .nav-link {
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-weight: 500;
  padding: 1rem 1.5rem;
  transition: all 0.2s ease-in-out;
}

.nav-tabs .nav-link:hover {
  border-color: transparent;
  color: #1f4bf1;
  background-color: rgba(31, 75, 241, 0.05);
}

.nav-tabs .nav-link.active {
  color: #1f4bf1;
  background-color: transparent;
  border-color: transparent transparent #1f4bf1 transparent;
  border-bottom: 3px solid #1f4bf1;
}

.nav-tabs .nav-link.active:hover {
  border-color: transparent transparent #1f4bf1 transparent;
}

.card-header-tabs {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;
}

.tab-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Search Input Styles */
.search-box-wrapper {
  position: relative;
}

.search-box-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
}

.search-box-input {
  padding-left: 38px;
}

.search-box-input:focus {
  border-color: #1f4bf1;
  box-shadow: 0 0 0 0.2rem rgba(31, 75, 241, 0.25);
}

/* The theme's global .btn has a 10px top/bottom margin, which throws off
   alignment when these buttons sit inline with .form-control date inputs */
.filter-actions {
  display: flex;
}

.filter-actions .filter-btn {
  margin: 0 0 0 8px;
  height: calc(1.5em + 0.75rem + 2px);
  display: inline-flex;
  align-items: center;
}

.filter-actions .filter-btn:first-child {
  margin-left: 0;
}

/* Smooth scrolling enhancement */
html {
  scroll-behavior: smooth;
}

/* Add scroll margin to account for fixed headers */
#reportContent, #crux, #gtmetrix, #wpo {
  scroll-margin-top: 100px;
}

/* View More Details button hover effect */
.btn-light.border:hover {
  background-color: #1f4bf1;
  color: white;
  border-color: #1f4bf1;
  transform: translateY(-1px);
  transition: all 0.2s ease-in-out;
}

/* Domain Selection Modal Styles */
.domain-selection-list {
  max-height: 400px;
  overflow-y: auto;
}

.domain-option {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 2px solid #e9ecef;
}

.domain-option:hover {
  border-color: #1f4bf1;
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.1);
}

.domain-option.selected {
  border-color: #1f4bf1;
  background-color: #f5f8ff;
  box-shadow: 0 2px 8px rgba(31, 75, 241, 0.15);
}

.domain-option .card-body {
  padding: 1rem;
}

.domain-option h6 {
  color: #1f4bf1;
  font-weight: 600;
}

.domain-option .text-muted {
  font-size: 0.875rem;
}

.domain-option .badge {
  font-size: 0.75rem;
}

/* Plugin Comment Styles */
.plugin-comment-cell {
  max-width: 250px;
}

.plugin-comment-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  color: #6c757d;
}

.plugin-comment-btn {
  transition: all 0.2s ease-in-out;
}

.plugin-comment-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(31, 75, 241, 0.2);
}
</style>
